'use client';

import Link from 'next/link';
import { FolderOpen, PanelLeft, PanelRight, FileText, MessageSquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PisrsIcon } from '@/components/icons/PisrsIcon';
import { SettingsDialog } from '@/components/settings/SettingsDialog';
import { InfoDialog } from '@/components/InfoDialog';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { UserMenu } from '@/components/auth/UserMenu';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useZadevaStore } from '@/lib/stores/zadeva-store';
import { useDocumentViewerStore } from '@/lib/stores/document-viewer-store';
import type { DossierCard } from '@/lib/stores/dossier-store';

// Stable empty array to prevent getServerSnapshot infinite loop during SSR
const EMPTY_DOSSIER: DossierCard[] = [];

export function Header() {
  const { toggleDossier } = useLayoutStore();
  const dossierCards = useZadevaStore((s) => {
    const z = s.zadeve.find((z) => z.id === s.activeZadevaId);
    return z?.dossierCards || EMPTY_DOSSIER;
  });

  const hasDocument = useDocumentViewerStore((s) => s.document !== null);
  const docViewerOpen = useDocumentViewerStore((s) => s.isOpen);
  const toggleDocViewer = useDocumentViewerStore((s) => s.togglePanel);

  return (
    <header className="h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-full items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2">
          <PisrsIcon className="h-6 w-6 text-primary" />
          <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-cormorant), serif' }}>TRIBONIANUS</span>
        </Link>

        {/* Navigation */}
        <nav className="flex items-center gap-2">
          {/* Nov pogovor — ustvari novo zadevo + jo aktivira */}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              const id = useZadevaStore.getState().createZadeva();
              useZadevaStore.getState().setActiveZadeva(id);
            }}
            title="Nov pogovor (nova zadeva)"
          >
            <MessageSquarePlus className="h-4 w-4" />
            <span className="hidden sm:inline">Nov pogovor</span>
          </Button>

          {/* Zadeve Toggle (left panel) */}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={toggleDossier}
          >
            <PanelLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Zadeve</span>
            {dossierCards.length > 0 && (
              <Badge variant="secondary" className="ml-0.5 text-[10px] h-4 px-1">
                {dossierCards.length}
              </Badge>
            )}
          </Button>

          {/* Dokument Toggle (right panel) */}
          <Button
            variant={docViewerOpen ? 'secondary' : 'ghost'}
            size="sm"
            className="gap-1.5"
            onClick={toggleDocViewer}
            disabled={!hasDocument}
            title={hasDocument ? (docViewerOpen ? 'Zapri dokument' : 'Odpri dokument') : 'Ni odprtega dokumenta'}
          >
            <PanelRight className="h-4 w-4" />
            <span className="hidden sm:inline">Dokument</span>
          </Button>

          {/* Separator */}
          <div className="h-6 w-px bg-border mx-1" />

          {/* Info — UI-INFO: zmogljivosti in omejitve */}
          <InfoDialog />

          {/* Settings */}
          <SettingsDialog />

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User Menu */}
          <UserMenu />
        </nav>
      </div>
    </header>
  );
}
